import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/locanest/Navbar";
import HomePage from "@/pages/HomePage";
import Listings from "@/pages/Listings";
import PropertyDetail from "@/pages/PropertyDetail";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import OwnerDashboard from "@/pages/OwnerDashboard";
import AddProperty from "@/pages/AddProperty";
import Favorites from "@/pages/Favorites";
import MapPage from "@/pages/MapPage";
import OwnerVerify from "@/pages/OwnerVerify";
import AdminPanel from "@/pages/AdminPanel";
import MyBookings from "@/pages/MyBookings";
import BookingDetail from "@/pages/BookingDetail";
import VendorsPage from "@/pages/VendorsPage";
import { TermsPage, PrivacyPage, RefundPage } from "@/pages/LegalPages";
import Footer from "@/components/locanest/Footer";
import HomepageNudge from "@/components/locanest/HomepageNudge";
import BottomNav from "@/components/locanest/BottomNav";
import ProfilePage from "@/pages/ProfilePage";
import MyChats from "@/pages/MyChats";
import SupportPage from "@/pages/SupportPage";
import AuthCallback from "@/pages/AuthCallback";

function AccessDenied({ requiredRole, currentUser, onLogout }) {
  return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center" data-testid="access-denied">
      <div className="text-xs uppercase tracking-[0.25em] text-[#C85A32]">Restricted area</div>
      <h1 className="font-display text-3xl md:text-4xl mt-3">
        {requiredRole === "admin" ? "Admin dashboard" : requiredRole === "owner" ? "Owner area" : "Tenant area"}
      </h1>
      <p className="text-[#5C6B7A] mt-3 leading-relaxed">
        You&rsquo;re signed in as <strong className="text-[#1A1F2C]">{currentUser.name}</strong> ({currentUser.role}).
        This page needs {requiredRole === "admin" || requiredRole === "owner" ? "an" : "a"} <strong>{requiredRole}</strong> account.
      </p>
      <div className="mt-6 rounded-md bg-[#F0EBE1] border border-[#1A1F2C]/10 p-4 text-sm text-[#1A1F2C] text-left">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#C85A32] font-semibold mb-1">What to do</div>
        Sign out of the current account, then sign in with your {requiredRole} credentials
        {requiredRole === "admin" && <> (owner of the platform)</>}.
      </div>
      <div className="mt-6 flex gap-3 justify-center">
        <button
          onClick={onLogout}
          className="px-4 py-2 rounded-md bg-[#C85A32] hover:bg-[#a8482a] text-white text-sm font-medium"
          data-testid="access-denied-logout"
        >
          Sign out &amp; switch account
        </button>
        <Link
          to="/"
          className="px-4 py-2 rounded-md border border-[#1A1F2C]/15 text-sm hover:bg-[#F0EBE1]"
          data-testid="access-denied-home"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

function Protected({ children, role }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-12 text-center text-[#5C6B7A]">Loading…</div>;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  // Admin can access any protected page; other roles only their own.
  if (role && user.role !== role && user.role !== "admin") {
    return <AccessDenied requiredRole={role} currentUser={user} onLogout={() => { logout(); }} />;
  }
  return children;
}

function Layout({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A1F2C] flex flex-col pb-16 md:pb-0">
      <Navbar />
      <main className="flex-1">{children}</main>
      <HomepageNudge />
      <Footer />
      <BottomNav />
    </div>
  );
}

function AppRoutes() {
  return (
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/auth/google" element={<AuthCallback />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPage />} />
            <Route path="/property/:id" element={<PropertyDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/favorites" element={<Protected role="tenant"><Favorites /></Protected>} />
            <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
            <Route path="/chats" element={<Protected><MyChats /></Protected>} />
            <Route path="/owner" element={<Protected role="owner"><OwnerDashboard /></Protected>} />
            <Route path="/owner/new" element={<Protected role="owner"><AddProperty /></Protected>} />
            <Route path="/owner/edit/:id" element={<Protected role="owner"><AddProperty /></Protected>} />
            <Route path="/owner/verify" element={<Protected role="owner"><OwnerVerify /></Protected>} />
            <Route path="/admin" element={<Protected role="admin"><AdminPanel /></Protected>} />
            <Route path="/bookings" element={<Protected role="tenant"><MyBookings /></Protected>} />
            <Route path="/bookings/:id" element={<Protected><BookingDetail /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <AppRoutes />
        </Layout>
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}
